/**
 * AuthContext.js - Authentication context provider
 *
 * Manages user authentication state across the app.
 * Supports admin preview mode: when previewTier is set, user.plan is overridden
 * client-side so admins can see what each tier looks like.
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import * as api from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewTier, setPreviewTierState] = useState(() => {
    return sessionStorage.getItem('adminPreviewTier') || null;
  });

  // Load token from localStorage on mount and fetch current plan
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      // Fetch current plan and flags from server
      api.getMe().then(data => {
        const updatedUser = {
          ...parsedUser,
          plan: data.plan,
          subscriptionStatus: data.subscriptionStatus,
          currentPeriodEnd: data.currentPeriodEnd,
          hasStripeSubscription: data.hasStripeSubscription,
          mustChangePassword: data.mustChangePassword,
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }).catch(() => {
        // Token may be expired — clear auth
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      });
    }

    setLoading(false);
  }, []);

  // Save token and user to localStorage
  const saveAuth = (newToken, newUser) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  // Clear auth
  const clearAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('adminPreviewTier');
    setToken(null);
    setUser(null);
    setPreviewTierState(null);
  };

  // Login
  const login = async (email, password) => {
    try {
      const response = await api.login(email, password);
      saveAuth(response.token, response.user);
      // Fetch plan after login
      try {
        const meData = await api.getMe();
        const updatedUser = {
          ...response.user,
          plan: meData.plan,
          subscriptionStatus: meData.subscriptionStatus,
          currentPeriodEnd: meData.currentPeriodEnd,
          hasStripeSubscription: meData.hasStripeSubscription,
          mustChangePassword: meData.mustChangePassword,
        };
        saveAuth(response.token, updatedUser);
      } catch (_) { /* plan fetch failed, user still logged in */ }
      return { success: true, mustChangePassword: response.mustChangePassword };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Signup
  const signup = async (email, password, name) => {
    try {
      const response = await api.signup(email, password, name);
      // New users always start on free plan
      saveAuth(response.token, { ...response.user, plan: 'free' });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Refresh user data from server (e.g. after profile update)
  const refreshUser = async () => {
    try {
      const data = await api.getMe();
      const updatedUser = {
        ...data.user,
        plan: data.plan,
        subscriptionStatus: data.subscriptionStatus,
        currentPeriodEnd: data.currentPeriodEnd,
        hasStripeSubscription: data.hasStripeSubscription,
        mustChangePassword: data.mustChangePassword,
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (_) { /* silently fail */ }
  };

  // Logout
  const logout = () => {
    clearAuth();
  };

  // Admin preview tier management
  const setPreviewTier = async (tier) => {
    await api.adminSetPreviewTier(tier);
    if (tier) {
      sessionStorage.setItem('adminPreviewTier', tier);
    } else {
      sessionStorage.removeItem('adminPreviewTier');
    }
    setPreviewTierState(tier);
  };

  const clearPreviewTier = () => {
    sessionStorage.removeItem('adminPreviewTier');
    setPreviewTierState(null);
  };

  // When preview tier is active, override user.plan so all existing plan checks work
  const effectiveUser = previewTier && user?.isAdmin
    ? { ...user, plan: previewTier }
    : user;

  const value = {
    user: effectiveUser,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    signup,
    logout,
    refreshUser,
    previewTier: user?.isAdmin ? previewTier : null,
    setPreviewTier,
    clearPreviewTier
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
